import { useState } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import { useTranslation } from 'react-i18next'
import { useSubscription } from '@/hooks/useSubscription'
import { supportMailto } from '@/config/contact'
import { billingService } from '@/services/billingService'

/**
 * A renewal is not going through (Z-74).
 *
 * Stripe retries a failed charge over several days and the account keeps the
 * period it paid for, so nothing is taken away at the moment the card is
 * declined. That grace is the reason this banner has to exist: without it the
 * first thing the customer hears about a dead card is the lock screen, days
 * later, with no idea what happened.
 *
 * Two states, because they need different things from the reader. A declined
 * charge is ours to chase; a 3-D Secure confirmation can only be finished by
 * the customer, in their bank's app, and saying "payment failed" there would
 * send them looking for a problem with the card that does not exist.
 *
 * The primary button now opens the Stripe customer portal (Z-95), so a dead
 * card can be swapped without waiting on support. The mail link stays as a
 * fallback for everything a card form cannot fix — a changed bank, a company
 * that needs a different invoice.
 */
export default function PaymentIssueBanner() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { paymentState, paymentProblemDays } = useSubscription()
  const [portalBusy, setPortalBusy] = useState(false)

  if (!paymentState) return null

  const actionRequired = paymentState === 'action_required'
  const tone = actionRequired ? c.warning : c.coral
  const Icon = actionRequired ? VerifiedUserOutlinedIcon : CreditCardOffIcon

  const openPortal = async () => {
    setPortalBusy(true)
    try {
      const url = await billingService.openBillingPortal()
      // Leaving the app entirely — no state set after this matters.
      window.location.href = url
    } catch {
      setPortalBusy(false)
    }
  }

  return (
    <Box
      sx={{
        mx: { xs: '20px', md: '32px' },
        mt: { xs: '16px', md: '20px' },
        px: '18px',
        py: '14px',
        borderRadius: '14px',
        bgcolor: alpha(tone, 0.08),
        border: `1px solid ${alpha(tone, 0.3)}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <Icon sx={{ fontSize: 20, color: tone, mt: '2px', flexShrink: 0 }} />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {actionRequired ? t('payment.actionRequiredTitle') : t('payment.failedTitle')}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: c.ink72 }}>
          {actionRequired ? t('payment.actionRequiredBody') : t('payment.failedBody')}
          {paymentProblemDays !== null && paymentProblemDays > 0
            ? ` ${t('payment.since', { count: paymentProblemDays })}`
            : ''}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
        <Button
          size="small"
          variant="contained"
          disabled={portalBusy}
          onClick={() => void openPortal()}
          sx={{ fontWeight: 700, fontSize: 13 }}
        >
          {portalBusy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : t('payment.updateCard')}
        </Button>
        <Button
          size="small"
          component="a"
          href={supportMailto(t('payment.mailSubject'))}
          sx={{ fontWeight: 700, fontSize: 13 }}
        >
          {t('payment.cta')}
        </Button>
      </Stack>
    </Box>
  )
}
